import { Phone, Wrench, ClipboardList, FileText, Clock, ShieldCheck } from "lucide-react";

const PROJECT_DETAILS = [
    "Model and size of your exhaust hood",
    "Model and size of your cooking appliance",
    "Fuel type — gas or electric",
    "Whether your plan calls for a central alarm",
    "Whether a manual pull station is required",
    "Any other details specific to your project",
];

const PROPOSAL_PROMISES = [
    {
        icon: FileText,
        title: "Free, written proposal",
        body: "Clear, itemized — parts, install, permits. No hidden line items.",
    },
    {
        icon: Clock,
        title: "Usually same-day turnaround",
        body: "Send your project details. You'll hear back from Tom directly — usually within hours.",
    },
    {
        icon: ShieldCheck,
        title: "Zero obligation",
        body: "A proposal is a starting point, not a contract. Compare us against anyone.",
    },
];

function PricingHeader() {
    return (
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                        / 04 — Get a Quote
                    </span>
                </div>
                <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                    No guesswork.
                    <br />
                    Just a{" "}
                    <span className="text-[#E53935] italic font-medium">written proposal</span>.
                </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
                <p className="text-base md:text-lg text-[#a1a1aa] leading-relaxed">
                    Every Kitchensafe install is unique. We don't quote until we understand your
                    hood, your appliance, and your plan — then you get a clear proposal in writing.
                </p>
            </div>
        </div>
    );
}

function IntakeChecklist() {
    return (
        <div className="lg:col-span-7 bg-[#141416] p-8 md:p-12">
            <div className="flex items-center gap-3 mb-2">
                <ClipboardList className="w-5 h-5 text-[#E53935]" strokeWidth={1.75} />
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                    What we need to quote your project
                </div>
            </div>
            <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mt-3 mb-8 max-w-md">
                Send us the basics. We'll handle the rest.
            </h3>
            <ul className="space-y-4">
                {PROJECT_DETAILS.map((item, i) => (
                    <li
                        key={item}
                        data-testid={`intake-${i}`}
                        className="flex items-start gap-4 border-t border-white/5 pt-4 first:border-t-0 first:pt-0"
                    >
                        <span className="font-mono-tech text-[11px] text-[#71717a] tabular-nums mt-1.5 flex-shrink-0">
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display text-base md:text-lg text-white leading-snug">
                            {item}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function PromiseCard({ promise }) {
    const Icon = promise.icon;
    return (
        <div
            data-testid={`promise-${promise.title}`}
            className="bg-[#0a0a0b] p-6 md:p-7 hover:bg-[#141416] transition-colors group"
        >
            <div className="w-10 h-10 flex items-center justify-center border border-white/10 group-hover:border-[#E53935]/50 group-hover:bg-[#E53935]/5 transition-all mb-4">
                <Icon className="w-4 h-4 text-white" strokeWidth={1.75} />
            </div>
            <div className="font-display font-bold text-base md:text-lg text-white mb-1.5 tracking-tight">
                {promise.title}
            </div>
            <p className="text-xs md:text-sm text-[#a1a1aa] leading-relaxed">{promise.body}</p>
        </div>
    );
}

function ProposalPromises() {
    return (
        <div className="lg:col-span-5 grid gap-px bg-white/5 border border-white/10">
            {PROPOSAL_PROMISES.map((p) => (
                <PromiseCard key={p.title} promise={p} />
            ))}
        </div>
    );
}

function CallTomFooter() {
    return (
        <div className="mt-px grid lg:grid-cols-12 gap-px bg-white/5 border border-white/10 border-t-0">
            <div className="lg:col-span-8 bg-[#141416] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="w-14 h-14 flex items-center justify-center border border-white/10 bg-[#0a0a0b] flex-shrink-0">
                    <Wrench className="w-6 h-6 text-[#E53935]" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935] mb-2">
                        Installation
                    </div>
                    <div className="font-display font-bold text-xl md:text-2xl text-white mb-1">
                        Quoted with the system.
                    </div>
                    <p className="text-sm text-[#a1a1aa] leading-relaxed max-w-xl">
                        Installation cost depends on permit fees, trip count, and the run length
                        from your control panel to the hood (up to 15 ft). It's built into the
                        same written proposal — one document, one number, no surprises.
                    </p>
                </div>
            </div>
            <div className="lg:col-span-4 bg-[#0a0a0b] p-8 md:p-10 flex flex-col justify-center">
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#71717a] mb-2">
                    Start the proposal
                </div>
                <div className="font-display font-bold text-lg text-white mb-4">
                    Call or text Tom directly
                </div>
                <a
                    href="tel:4806945234"
                    data-testid="pricing-call-cta"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#E53935] hover:bg-[#F44336] text-white font-bold rounded-sm transition-colors"
                >
                    <Phone className="w-4 h-4" />
                    480-694-5234
                </a>
            </div>
        </div>
    );
}

export default function Pricing() {
    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="relative py-20 md:py-24 border-t border-white/10"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <PricingHeader />
                <div className="grid lg:grid-cols-12 gap-px bg-white/5 border border-white/10">
                    <IntakeChecklist />
                    <ProposalPromises />
                </div>
                <CallTomFooter />
            </div>
        </section>
    );
}
